import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import SegmentedTabs from '../../../reusable/SegmentedTabs/SegmentedTabs';
import { useTopbarSlot } from '../../../layout/TopbarSlot';
import {
  useGetCompetitionDetailQuery,
  useGetCompetitionLeaderboardQuery,
} from '../../../store/api/competitionsApi';
import { debounce } from '../../../utilities/debounce';
import useCountdown from '../useCountdown';
import { statusFromApi, titleOf } from '../competition';
import LeaderboardTable from '../LeaderboardTable';
import { TopSkeleton } from './OverviewSkeleton';
import { NA, usd, signedUsd } from '../../../utilities/format';
import { t } from '../../../i18n/i18n';
import './Overview.scss';

const pad = (n) => String(n).padStart(2, '0');

/** Rows per request, and so per page — the endpoint's own default page size. */
const ROWS_PER_PAGE = 10;
/** Offered in the board's footer; the chosen one is the request's `page_size`. */
const ROWS_PER_PAGE_OPTIONS = [10, 20, 30, 50];

const UNITS = [
  ['days', 'd'],
  ['hours', 'h'],
  ['minutes', 'm'],
  ['seconds', 's'],
];

/**
 * The endpoint's own `filter`, which `meta` echoes back — "all" is the value
 * it answers with when nothing is set.
 * ponytail: "all" is the only one seen in a response; the other three are the
 * row statuses lowercased, which is the shape that one implies.
 */
const STATUS_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Trading', value: 'trading' },
  { label: 'Eliminated', value: 'eliminated' },
  { label: 'Completed', value: 'completed' },
];

/** Long enough that a typed word is one request, short enough to feel live. */
const SEARCH_DELAY = 300;

/** A count the design writes with thousands separators. */
const count = (n) => (n == null ? NA : Number(n).toLocaleString('en-US'));

/** "+2.14%" — `return_percentage` arrives at full float precision. */
const pct = (n) => (n == null ? NA : `${n < 0 ? '' : '+'}${Number(n).toFixed(2)}%`);

/** How far through its window the competition is, for the hero's rail. */
const elapsedPct = (start, end) => {
  const span = end - start;
  if (!Number.isFinite(span) || span <= 0) return 0;
  return Math.min(Math.max(((Date.now() - start) / span) * 100, 0), 100);
};

export default function CompetitionOverview() {
  // ---- Overview / Competitions tablist — lives in the top bar, 1:9020 ----
  const location = useLocation();
  const navigate = useNavigate();
  const activeView = location.pathname === '/competition' ? 'Overview' : 'Competitions';

  useTopbarSlot(
    useCallback(
      () => (
        <SegmentedTabs
          size="lg"
          radius="tab-track"
          className="competition-toggle"
          options={['Overview', 'Competitions'].map((v) => ({ value: v, label: t(v) }))}
          value={activeView}
          onChange={(next) => navigate(next === 'Competitions' ? '/competition/all' : '/competition')}
        />
      ),
      [activeView, navigate]
    ),
    [activeView]
  );

  // ---- Top section — 1:14844, /v2/competition/detail/ ----
  // One call answers both cards: the trader's own figures are nested inside
  // the competition record, under `user`.
  const { data, isLoading, isError } = useGetCompetitionDetailQuery();
  const detail = data?.competition ?? null;
  const user = detail?.user ?? null;

  const upcoming = statusFromApi(detail?.status) === 'upcoming';

  // `starts_at` / `ends_at` are the competition's own instants, sent with an
  // offset, so the clock and the rail are right wherever the viewer is. (They
  // replaced the `countdown.total_seconds` this used to count down from.)
  //
  // Not interchangeable with `Start_date` / `End_Date` below, which are plain
  // dates: `End_Date` is midnight *opening* the closing day and `ends_at` is
  // the end of it, nearly a day apart. The dates write the label; the instants
  // do the arithmetic.
  const target = Date.parse((upcoming ? detail?.starts_at : detail?.ends_at) ?? '');
  const countdown = useCountdown(Number.isFinite(target) ? target : 0);

  const elapsed = upcoming
    ? 0
    : elapsedPct(Date.parse(detail?.starts_at ?? ''), Date.parse(detail?.ends_at ?? ''));

  const start = Date.parse(detail?.Start_date ?? '');
  const end = Date.parse(detail?.End_Date ?? '');

  // ---- Leaderboard — 1:14844, /v2/competition/leaderboard/ ----
  // The board is per competition and the detail call is what names which one,
  // so this waits on it rather than guessing.
  //
  // Paging, filtering and search are all the endpoint's: it holds 6,726
  // traders and answers one page of them, so doing any of the three to the
  // page in hand would be describing the page as if it were the board.
  const [query, setQuery] = useState({
    page: 1,
    pageSize: ROWS_PER_PAGE,
    filter: 'all',
    search: '',
  });

  /** Anything but the page number is a different board, so it lands on page 1. */
  const setQueryParam = (key, value) => setQuery((prev) => ({ ...prev, page: 1, [key]: value }));

  const onSearch = useMemo(
    () => debounce((value) => setQueryParam('search', value.trim()), SEARCH_DELAY),
    []
  );

  const {
    data: board,
    isFetching: boardFetching,
    isUninitialized: boardUninitialized,
  } = useGetCompetitionLeaderboardQuery(
    {
      competition_id: detail?.id,
      page: query.page,
      // The footer's own selection — 6,726 traders is 673 pages at ten a time.
      page_size: query.pageSize,
      filter: query.filter,
      search: query.search,
    },
    { skip: !detail?.id }
  );

  // The board is skipped until the detail call names a competition, so waiting
  // covers that call too — a skipped query reports neither loading nor
  // fetching, and the table would flash "No traders" before the request it
  // depends on had even been made. Once there is no competition to ask about,
  // it stops waiting and the empty message is the right answer.
  const boardLoading =
    isLoading || boardFetching || (boardUninitialized && Boolean(detail?.id));

  // Straight from the payload — `LeaderboardTable`'s columns are keyed on its
  // field names. `leaderboard` is the paged board: `results` are the rows and
  // `meta` is the page it came from.
  const rows = useMemo(() => board?.leaderboard?.results ?? [], [board]);
  const meta = board?.leaderboard?.meta;
  const currentUser = board?.user ?? null;

  const primaryStats = [
    {
      label: 'Rank',
      value: user?.rank != null ? `#${user.rank}` : NA,
      // `rank_of` is the field they are ranked against — the entrant count.
      note:
        user?.rank_of != null
          ? `${t('of {{count}}', { count: count(user.rank_of) })}${
              user.top_percent != null
                ? ` · ${t('top {{percent}}%', { percent: user.top_percent })}`
                : ''
            }`
          : NA,
    },
    {
      label: 'Profit',
      value: signedUsd(user?.profit),
      note:
        user?.return_percentage != null
          ? t('{{percent}} return', { percent: pct(user.return_percentage) })
          : NA,
      tone: user?.profit < 0 ? 'down' : 'up',
    },
    {
      label: 'Trades',
      // `total_trades` counts the open ones too, which is what the note says.
      value: user?.total_trades ?? NA,
      note:
        user?.open_trades != null
          ? t('{{count}} still open', { count: user.open_trades })
          : NA,
    },
    {
      label: 'Win rate',
      value: user?.win_rate != null ? `${user.win_rate}%` : NA,
      note:
        user?.wins != null && user?.losses != null
          ? t('{{wins}}W / {{losses}}L', { wins: user.wins, losses: user.losses })
          : NA,
    },
    {
      label: 'Drawdown left',
      value: usd(user?.drawdown_left),
      note:
        user?.drawdown_limit != null
          ? t('of {{amount}} limit', { amount: usd(user.drawdown_limit) })
          : NA,
    },
  ];

  const inlineStats = [
    { label: 'Gross profit', value: signedUsd(user?.gross_profit), tone: 'up' },
    { label: 'Gross loss', value: signedUsd(user?.gross_loss), tone: 'down' },
    { label: 'Best trade', value: signedUsd(user?.best_trade), tone: 'up' },
    { label: 'Worst trade', value: signedUsd(user?.worst_trade), tone: 'down' },
  ];

  // The name carries the challenge as a suffix ("September 2026 Competition -
  // 100K Challenge") while `account_size_label` says the same thing in its own
  // field, which is where the subtitle draws it from.
  const subtitle = [
    detail?.account_size_label
      ? t('{{size}} Challenge', { size: detail.account_size_label })
      : null,
    detail?.trader_count != null
      ? t('{{count}} traders', { count: count(detail.trader_count) })
      : null,
    detail?.prize_pool != null
      ? t('{{amount}} prize pool', { amount: usd(detail.prize_pool) })
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const range =
    Number.isFinite(start) && Number.isFinite(end)
      ? `${format(start, 'dd MMM')} – ${format(end, 'dd MMM yyyy')}`
      : null;

  return (
    <div className="competition-overview">
      {isLoading ? (
        <TopSkeleton />
      ) : isError || !user?.joined ? (
        // The card's whole premise is "competing now", so there is no hero to
        // draw when the trader has not entered. The board below still is — it
        // only needs the competition's id, which came back either way.
        <p className="co-empty">
          {isError
            ? t('We could not load your current competition.')
            : t('You are not competing in anything right now.')}
        </p>
      ) : (
        <section className="co-top">
          {/* ---- Competing now — hero card, 1:14844 ---- */}
          <section className="cnc">
            <div className="cnc__intro">
              <span className="cnc__eyebrow">{t('Competing now')}</span>
              <h2 className="cnc__title">{titleOf(detail.competition_name) || NA}</h2>
              {subtitle && <p className="cnc__subtitle">{subtitle}</p>}
            </div>

            <div className="cnc__countdown">
              <div className="cnc__timer">
                <span className="cnc__timer-label">{upcoming ? t('Starts in') : t('Ends in')}</span>
                <div className="cnc__units">
                  {UNITS.map(([key, label]) => (
                    <div className="cnc__unit" key={key}>
                      <span className="cnc__unit-value">{pad(countdown[key])}</span>
                      <span className="cnc__unit-label">{t(label)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {range && <span className="cnc__range">{range}</span>}
            </div>

            <span className="cnc__rail" role="img" aria-label={t('{{percent}}% elapsed', { percent: Math.round(elapsed) })}>
              <span className="cnc__rail-fill" style={{ width: `${elapsed}%` }} />
            </span>
          </section>

          {/* ---- Stats summary card, 1:14844 ---- */}
          <section className="scs">
            <div className="scs__flex">
              {primaryStats.map((s) => (
                <div className="scs__cell" key={s.label}>
                  <span className="scs__label">{t(s.label)}</span>
                  <span className={`scs__value${s.tone ? ` is-${s.tone}` : ''}`}>{s.value}</span>
                  <span className={`scs__note${s.tone ? ` is-${s.tone}` : ''}`}>{s.note}</span>
                </div>
              ))}
            </div>

            <div className="scs__inline">
              {inlineStats.map((s) => (
                <div className="scs__inline-item" key={s.label}>
                  <span className="scs__inline-label">{t(s.label)}</span>
                  <span className={`scs__inline-value is-${s.tone}`}>{s.value}</span>
                </div>
              ))}
            </div>
          </section>
        </section>
      )}

      <LeaderboardTable
        rows={rows}
        currentUser={currentUser}
        meta={meta}
        page={query.page}
        onPageChange={(page) => setQuery((prev) => ({ ...prev, page }))}
        pageSize={query.pageSize}
        pageSizeOptions={ROWS_PER_PAGE_OPTIONS}
        // A different number of rows is a different page 1.
        onPageSizeChange={(size) =>
          setQuery((prev) => ({ ...prev, page: 1, pageSize: size }))
        }
        updatedLive
        loading={boardLoading}
        count={boardLoading ? '—' : meta?.total ?? rows.length}
        searchPlaceholder={t('Search traders')}
        onSearch={onSearch}
        filter={{
          options: STATUS_OPTIONS.map((o) => ({ ...o, label: t(o.label) })),
          value: query.filter,
          onChange: (value) => setQueryParam('filter', value),
        }}
      />
    </div>
  );
}
